/* SettingsMultipliers — V23.15.25
 *
 * UI Settings pour la calibration des multiplicateurs Brute/Net (Doctrine 18).
 * Affiche les 6 facteurs de friction (KYC, conformité, défauts, churn, fraude, complexité)
 * avec leur valeur actuelle, leur défaut code, et un slider/input pour ajuster
 * factor / factor_per_unit / floor (selon le type de multiplier).
 *
 * Admin workspace requis (PATCH /api/workspace/valuations/multipliers/{key}).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Sliders,
  RotateCcw,
  Save,
  Info,
  CheckCircle2,
  Loader2,
} from "lucide-react";

export default function SettingsMultipliers() {
  const [items, setItems] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [drafts, setDrafts] = useState({});  // { key: { factor, factor_per_unit, floor } }

  const load = async () => {
    try {
      const r = await api.get("/workspace/valuations/multipliers");
      const list = r.data?.items || [];
      setItems(list);
      // Initialise les drafts avec les valeurs actuelles
      const d = {};
      list.forEach((it) => {
        d[it.key] = {
          factor: it.factor ?? "",
          factor_per_unit: it.factor_per_unit ?? "",
          floor: it.floor ?? "",
        };
      });
      setDrafts(d);
    } catch (e) {
      notify.error(fmtError(e, "Chargement des multiplicateurs impossible."));
    }
  };
  useEffect(() => { load(); }, []);

  const setDraftField = (key, field, value) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
  };

  const isDirty = (it) => {
    const d = drafts[it.key] || {};
    return (
      _toNum(d.factor) !== it.factor ||
      _toNum(d.factor_per_unit) !== it.factor_per_unit ||
      _toNum(d.floor) !== it.floor
    );
  };

  const save = async (it) => {
    const d = drafts[it.key] || {};
    const payload = {};
    if (d.factor !== "" && d.factor != null) payload.factor = parseFloat(d.factor);
    if (d.factor_per_unit !== "" && d.factor_per_unit != null) payload.factor_per_unit = parseFloat(d.factor_per_unit);
    if (d.floor !== "" && d.floor != null) payload.floor = parseFloat(d.floor);
    // Validation [0..1]
    for (const [k, v] of Object.entries(payload)) {
      if (Number.isNaN(v) || v < 0 || v > 1) {
        notify.error(`${k} doit être un nombre entre 0 et 1`);
        return;
      }
    }
    setBusyKey(it.key);
    try {
      await api.patch(`/workspace/valuations/multipliers/${it.key}`, payload);
      notify.success("Multiplicateur mis à jour.");
      await load();
    } catch (e) {
      notify.error(fmtError(e, "Mise à jour impossible."));
    } finally {
      setBusyKey(null);
    }
  };

  const resetToDefault = async (it) => {
    if (!it.overridden) return;
    if (!confirm(`Revenir au défaut pour "${it.label}" ?`)) return;
    const payload = {};
    if (it.default?.factor != null) payload.factor = it.default.factor;
    if (it.default?.factor_per_unit != null) payload.factor_per_unit = it.default.factor_per_unit;
    if (it.default?.floor != null) payload.floor = it.default.floor;
    setBusyKey(it.key);
    try {
      await api.patch(`/workspace/valuations/multipliers/${it.key}`, payload);
      notify.success("Multiplicateur réinitialisé.");
      await load();
    } catch (e) {
      notify.error(fmtError(e, "Réinitialisation impossible."));
    } finally {
      setBusyKey(null);
    }
  };

  if (items === null) {
    return (
      <div className="noam-card p-8 flex items-center gap-2 text-sm text-zinc-300">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-5xl" data-testid="settings-multipliers-page">
      <div>
        <div className="flex items-center gap-2 noam-overline text-[#378ADD]">
          <Sliders className="w-3 h-3" /> Doctrine 18 · Brute/Net
        </div>
        <h1 className="noam-display text-2xl font-bold tracking-tight mt-1">
          Calibration des multiplicateurs de friction
        </h1>
        <p className="text-sm text-zinc-300 mt-2 max-w-2xl leading-relaxed">
          Ces 6 facteurs réduisent la valeur théorique (« Brute ») de votre marché en valeur
          réellement capturable (« Net »). Plus le facteur est proche de 1, moins la friction
          réduit la valeur. Calibration suggérée après 4 semaines d&apos;usage pour aligner les
          défauts code aux frictions réelles de votre organisation.
        </p>
        <div className="mt-3 px-3 py-2 rounded-md bg-[#378ADD]/8 border border-[#378ADD]/25 text-[11px] text-zinc-200 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 text-[#378ADD] mt-0.5 flex-shrink-0" />
          <span>
            Toutes les valeurs sont normalisées entre 0 et 1. Les modifications sont appliquées
            immédiatement aux nouveaux calculs Brute/Net dans Origination, Bras Armé et le
            Générateur d&apos;opportunités.
          </span>
        </div>
      </div>

      <div className="space-y-3" data-testid="multipliers-list">
        {items.map((it) => (
          <MultiplierRow
            key={it.key}
            item={it}
            draft={drafts[it.key] || {}}
            dirty={isDirty(it)}
            busy={busyKey === it.key}
            onChange={(field, value) => setDraftField(it.key, field, value)}
            onSave={() => save(it)}
            onReset={() => resetToDefault(it)}
          />
        ))}
      </div>
    </div>
  );
}

function MultiplierRow({ item, draft, dirty, busy, onChange, onSave, onReset }) {
  // Détermine les champs disponibles selon le type de multiplier
  const hasFactor = item.factor != null;
  const hasFactorPerUnit = item.factor_per_unit != null;
  const hasFloor = item.floor != null;

  return (
    <div
      className="noam-card p-4 flex items-start gap-4 flex-wrap"
      data-testid={`multiplier-row-${item.key}`}
    >
      <div className="flex-1 min-w-[260px]">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-zinc-100">{item.label}</h3>
          {item.overridden && (
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EF9F27]/15 text-[#EF9F27]"
              data-testid={`multiplier-overridden-${item.key}`}
            >
              Calibré
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-300 mt-1 leading-relaxed">{item.description}</p>
        <div className="text-[10px] text-zinc-500 mt-1.5">
          Source : {item.source}
        </div>
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        {hasFactor && (
          <NumField
            label="Facteur"
            value={draft.factor}
            onChange={(v) => onChange("factor", v)}
            defaultVal={item.default?.factor}
            testId={`multiplier-factor-${item.key}`}
          />
        )}
        {hasFactorPerUnit && (
          <NumField
            label="Par unité"
            value={draft.factor_per_unit}
            onChange={(v) => onChange("factor_per_unit", v)}
            defaultVal={item.default?.factor_per_unit}
            testId={`multiplier-per-unit-${item.key}`}
          />
        )}
        {hasFloor && (
          <NumField
            label="Plancher"
            value={draft.floor}
            onChange={(v) => onChange("floor", v)}
            defaultVal={item.default?.floor}
            testId={`multiplier-floor-${item.key}`}
          />
        )}
        <div className="flex gap-1.5 ml-2">
          {item.overridden && (
            <button
              type="button"
              onClick={onReset}
              disabled={busy}
              className="text-[11px] px-2.5 py-1.5 rounded-md border border-white/10 text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid={`multiplier-reset-${item.key}`}
              title="Revenir au défaut"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={!dirty || busy}
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#378ADD] text-white hover:bg-[#378ADD]/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
            data-testid={`multiplier-save-${item.key}`}
          >
            {busy ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : dirty ? (
              <Save className="w-3 h-3" />
            ) : (
              <CheckCircle2 className="w-3 h-3" />
            )}
            {busy ? "..." : dirty ? "Enregistrer" : "Aligné"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, defaultVal, testId }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[9px] uppercase tracking-wider text-zinc-400">
        {label}
        {defaultVal != null && (
          <span className="text-zinc-600 normal-case ml-1">(déf. {defaultVal})</span>
        )}
      </label>
      <input
        type="number"
        min="0"
        max="1"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="noam-input text-xs w-24"
        data-testid={testId}
      />
    </div>
  );
}

function _toNum(v) {
  if (v === "" || v == null) return null;
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
}
